import { useState, useEffect } from 'react';
import { aiListAuditLogs, aiGetAuditLog, aiAuditCsvUrl } from '../services/api';
import { ShieldCheck, Download, Robot, Check, X as XIcon, Eye, Warning } from '@phosphor-icons/react';

export default function AIAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await aiListAuditLogs({ limit: 200 });
      setLogs(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const viewDetail = async (id) => {
    try {
      const res = await aiGetAuditLog(id);
      setSelectedLog(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('de-DE') : '—';

  return (
    <div className="space-y-6" data-testid="ai-audit-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            KI-Prüf-Protokoll
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base flex items-center gap-1.5">
            <ShieldCheck weight="bold" className="w-4 h-4" />
            DSGVO Art. 30 – Verarbeitungsverzeichnis aller KI-Anfragen
          </p>
        </div>
        <a
          href={aiAuditCsvUrl()}
          target="_blank"
          rel="noreferrer"
          className="bg-slate-900 text-white px-6 py-2.5 font-medium hover:bg-slate-800 flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="audit-csv-download"
        >
          <Download weight="bold" className="w-4 h-4" />
          CSV exportieren
        </a>
      </div>

      <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
        <p>
          Hier sehen Sie eine vollständige Liste aller KI-Prüfungen. Vor dem Versand wurden alle Personen-, Adress- und Firmenangaben anonymisiert.
          Sie können jeden Eintrag öffnen, um die exakt gesendeten Daten und die KI-Antwort einzusehen.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <Robot weight="light" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Noch keine Einträge</h3>
          <p className="text-slate-500">Sobald Sie auf der Belege-Seite die KI-Prüfung verwenden, erscheinen die Einträge hier.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Zeitpunkt</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Provider / Modell</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">PII entfernt</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Vorschlag</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Status</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map(l => {
                const piiCount = l.pii_counts_removed ? Object.values(l.pii_counts_removed).reduce((a, b) => a + b, 0) : 0;
                return (
                  <tr key={l.id} className="hover:bg-gray-50" data-testid={`audit-row-${l.id}`}>
                    <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{formatDate(l.created_at)}</td>
                    <td className="py-3 px-4 text-slate-600 text-xs">
                      <span className="font-medium uppercase">{l.provider}</span>
                      <br />
                      <span className="font-mono">{l.model?.split('/').pop()}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-block bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs font-medium">{piiCount}</span>
                    </td>
                    <td className="py-3 px-4 text-slate-700">
                      {l.suggestion?.kostenstelle_name || '—'}
                      {typeof l.suggestion?.konfidenz === 'number' && (
                        <span className="text-xs text-slate-500 ml-1">({Math.round(l.suggestion.konfidenz * 100)}%)</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {l.error ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium">
                          <Warning weight="bold" className="w-3.5 h-3.5" /> Fehler
                        </span>
                      ) : l.accepted ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                          <Check weight="bold" className="w-3.5 h-3.5" /> Übernommen
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-500 text-xs">
                          <XIcon weight="bold" className="w-3.5 h-3.5" /> Nicht übernommen
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => viewDetail(l.id)}
                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        data-testid={`audit-detail-${l.id}`}
                      >
                        <Eye weight="bold" className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedLog && <AuditDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
    </div>
  );
}

function AuditDetailModal({ log, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="audit-detail-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="font-heading text-lg font-bold text-slate-900">Audit-Eintrag</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <XIcon weight="bold" className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Zeitpunkt</p>
              <p className="font-mono">{log.created_at && new Date(log.created_at).toLocaleString('de-DE')}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Provider / Modell</p>
              <p className="font-mono">{log.provider} / {log.model}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Beleg-ID</p>
              <p className="font-mono text-xs break-all">{log.receipt_id}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Status</p>
              <p>{log.error ? 'Fehler' : log.accepted ? 'Übernommen' : 'Verworfen'}</p>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Entfernte PII (vor Versand)</p>
            <div className="flex flex-wrap gap-1.5">
              {log.pii_counts_removed && Object.keys(log.pii_counts_removed).length > 0 ? (
                Object.entries(log.pii_counts_removed).map(([k, v]) => (
                  <span key={k} className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5">{k}: {v}</span>
                ))
              ) : <span className="text-sm text-slate-500">Keine PII gefunden</span>}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Gesendete Daten</p>
            <pre className="bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
              {JSON.stringify(log.sent_payload, null, 2)}
            </pre>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">KI-Antwort</p>
            <pre className="bg-violet-50 border border-violet-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
              {JSON.stringify(log.ai_response_parsed || log.ai_response_raw, null, 2)}
            </pre>
          </div>

          {log.error && (
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-red-500 mb-1">Fehler</p>
              <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-800">{log.error}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
